import { Box, Tooltip, Typography, useTheme } from '@mui/material';

import { useOneMonthTimesheet } from '../hooks';
import { statusVisual } from '../statusTokens';
import DayTooltipContent from './DayTooltipContent';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS_COMPACT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/**
 * Calendar-style month grid (like the dashboard calendar): 7 columns,
 * one coloured cell per day with the day number and a rich tooltip.
 *
 * `compact` renders the small variant used by the yearly 12-month wall.
 * Pass either a pre-built `daySheet` (from buildMonthDaySheet) or raw
 * `entries` + `yearMonth`.
 */
const MonthCalendar = ({
    entries,
    yearMonth,
    daySheet: daySheetProp,
    title,
    compact = false,
    sx
}) => {
    const { palette } = useTheme();
    const computedSheet = useOneMonthTimesheet(
        daySheetProp ? [] : entries || [],
        yearMonth
    );
    const daySheet = daySheetProp || computedSheet;

    if (!daySheet.length) return null;

    const leadingBlanks = daySheet[0].weekday; // 0 = Sunday
    const weekdayLabels = compact ? WEEKDAYS_COMPACT : WEEKDAYS;

    return (
        <Box sx={{ width: '100%', ...sx }}>
            {title && (
                <Typography
                    variant={compact ? 'subtitle2' : 'h6'}
                    sx={{ fontWeight: 600, mb: compact ? 0.5 : 1 }}
                >
                    {title}
                </Typography>
            )}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: compact ? '3px' : { xs: '4px', sm: '6px' }
                }}
            >
                {weekdayLabels.map((label, index) => (
                    <Typography
                        key={`${label}-${index}`}
                        variant='caption'
                        sx={{
                            textAlign: 'center',
                            color: 'text.secondary',
                            fontWeight: 600,
                            fontSize: compact ? 9 : 11,
                            pb: compact ? 0 : 0.5
                        }}
                    >
                        {label}
                    </Typography>
                ))}

                {Array.from({ length: leadingBlanks }, (_, index) => (
                    <Box key={`blank-${index}`} />
                ))}

                {daySheet.map((day) => {
                    const visual = statusVisual(palette, day.status);
                    return (
                        <Tooltip
                            key={day.isoDate}
                            title={<DayTooltipContent day={day} />}
                        >
                            <Box
                                sx={{
                                    aspectRatio: '1 / 1',
                                    borderRadius: compact ? 0.5 : 1,
                                    backgroundColor: visual.bg,
                                    color: visual.text,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: compact
                                        ? 10
                                        : { xs: 11, sm: 13 },
                                    fontWeight: 600,
                                    lineHeight: 1,
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    minWidth: 0,
                                    '&:hover': {
                                        outline: `2px solid ${palette.primary.main}`,
                                        outlineOffset: '-1px'
                                    }
                                }}
                            >
                                {day.dayOfMonth}
                            </Box>
                        </Tooltip>
                    );
                })}
            </Box>
        </Box>
    );
};

export { MonthCalendar as default, MonthCalendar };
